import React from 'react';
import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

interface ModernAreaChartProps {
  title?: string;
  data: any[];
  areas: {
    dataKey: string;
    name: string;
    color: string;
    fillOpacity?: number;
    strokeWidth?: number;
    strokeDasharray?: string;
  }[];
  xAxisDataKey: string;
  formatYLabel?: (value: number) => string;
  formatTooltip?: (value: number, name: string) => string;
  stacked?: boolean;
}

const ModernAreaChart: React.FC<ModernAreaChartProps> = ({
  title,
  data,
  areas,
  xAxisDataKey,
  formatYLabel,
  formatTooltip,
  stacked = false
}) => {
  const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
      // Filter out series with strokeWidth or fillOpacity of 0 (hidden series)
      const visiblePayload = payload.filter(entry => {
        const matchingArea = areas.find(area => area.dataKey === entry.dataKey);
        return matchingArea && 
               ((matchingArea.strokeWidth === undefined || matchingArea.strokeWidth > 0) &&
                (matchingArea.fillOpacity === undefined || matchingArea.fillOpacity > 0));
      });

      if (visiblePayload.length === 0) return null;

      return (
        <div 
          className="bg-white p-4 rounded-lg shadow-xl border border-gray-200"
          style={{ 
            zIndex: 9999, 
            pointerEvents: 'auto',
            minWidth: '200px'
          }}
        >
          <p className="text-gray-700 font-medium mb-2">{`Año ${label}`}</p>
          <div className="space-y-2">
            {visiblePayload.map((entry, index) => (
              <div 
                key={`item-${index}`} 
                className="flex items-center gap-2"
              >
                <div 
                  className="w-4 h-4 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <p className="text-sm">
                  <span className="font-medium">{entry.name}: </span>
                  <span className="font-bold">
                    {formatTooltip 
                      ? formatTooltip(entry.value as number, entry.name as string) 
                      : entry.value}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full">
      {title && (
        <h3 className="text-center text-lg font-medium text-gray-800 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <RechartsAreaChart
          data={data}
          margin={{
            top: 20,
            right: 50,
            left: 50,
            bottom: 10,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey={xAxisDataKey} 
            tick={{ fill: '#4b5563' }}
            axisLine={{ stroke: '#9ca3af' }}
            tickMargin={10}
            padding={{ left: 10, right: 10 }}
          />
          <YAxis 
            tickFormatter={formatYLabel}
            tick={{ fill: '#4b5563' }}
            axisLine={{ stroke: '#9ca3af' }}
            tickMargin={10}
            width={80}
          />
          <Tooltip 
            content={<CustomTooltip />} 
            wrapperStyle={{ zIndex: 9999, pointerEvents: 'auto' }}
            cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
            isAnimationActive={false}
          />
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="circle"
            iconSize={10}
          />
          {areas.map((area, index) => (
            <Area
              key={index}
              type="monotone"
              dataKey={area.dataKey}
              name={area.name}
              stackId={stacked ? "stack" : undefined}
              stroke={area.color}
              fill={area.color}
              fillOpacity={area.fillOpacity || 0.6}
              strokeWidth={area.strokeWidth || 2}
              strokeDasharray={area.strokeDasharray}
              animationDuration={800}
              animationEasing="ease-out"
              animationBegin={index * 200}
              className="hover:filter hover:brightness-110 transition-all duration-300"
            />
          ))}
        </RechartsAreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ModernAreaChart;